// components/CoursePlanner/SemesterHeaderMenu.tsx
import { useState, useRef, useEffect } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { MoreVertical } from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmContext';

export interface SemesterHeaderMenuProps {
  semester: string;
  onAddPlaceholder?: ((semester: string) => void) | null;
  onClearSemester?: (semester: string) => void;
  onDeleteSemester?: (semester: string) => void;
  courseCount: number;
  canDelete?: boolean;
  isCurrentSemester?: boolean;
}

const SemesterHeaderMenu = ({
  semester,
  onAddPlaceholder,
  onClearSemester,
  onDeleteSemester,
  courseCount,
  canDelete = true,
  isCurrentSemester = false,
}: SemesterHeaderMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { confirm } = useConfirm();

  useClickOutside(menuRef, () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleAction = (action: () => void | Promise<void>): void => {
    void action();
    setIsOpen(false);
  };

  const handleDeleteSemester = async (): Promise<void> => {
    const message =
      courseCount > 0
        ? `Delete this semester and its ${courseCount} course${courseCount !== 1 ? 's' : ''}?`
        : 'Are you sure you want to delete this semester?';

    const confirmed = await confirm({
      title: 'Delete Semester',
      message,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      onDeleteSemester && onDeleteSemester(semester);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1 rounded hover:bg-gray-200 hover:bg-opacity-50 transition-colors"
        aria-label="Semester options"
      >
        <MoreVertical className="h-4 w-4 text-gray-600" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {onAddPlaceholder && (
            <button
              onClick={() => handleAction(() => onAddPlaceholder(semester))}
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
            >
              Add Placeholder Course
            </button>
          )}

          {courseCount > 0 && (
            <>
              <div className="border-t border-gray-100 my-1" />

              <button
                onClick={() =>
                  handleAction(async () => {
                    const confirmed = await confirm({
                      title: 'Clear Semester',
                      message: `Clear all ${courseCount} courses from this semester?`,
                      confirmText: 'Clear All',
                      variant: 'danger',
                    });
                    if (confirmed) {
                      onClearSemester && onClearSemester(semester);
                    }
                  })
                }
                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors text-red-600"
              >
                Clear Semester
              </button>
            </>
          )}

          {onDeleteSemester && (
            <>
              <div className="border-t border-gray-100 my-1" />

              {canDelete && !isCurrentSemester && (
                <button
                  onClick={() => handleAction(handleDeleteSemester)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 transition-colors text-red-600"
                >
                  Delete Semester
                </button>
              )}

              {(!canDelete || isCurrentSemester) && (
                <div className="px-3 py-2 text-sm text-gray-400 cursor-not-allowed">
                  {isCurrentSemester ? 'Current Semester' : 'Cannot Delete'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SemesterHeaderMenu;
